import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";

import type { DocumentsItemDto, LanguageIdDto } from "@/api/types";
import { type Breadcrumb, Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button, buttonVariants } from "@/design-system/Button";
import { Divider } from "@/design-system/Divider";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { useTranslation } from "@/translations";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

import { DocumentsFileIcon } from "./DocumentsFileIcon";

type DocumentsMoveItemModalProps = ModalBaseProps & {
  item: DocumentsItemDto | undefined;
  folderId: string;
};

const ROOT_FOLDER_ID = "root";

export function DocumentsMoveItemModal({
  isOpened,
  onOpenChange,
  item,
  folderId,
}: DocumentsMoveItemModalProps): React.ReactNode {
  const [currentFolderId, setCurrentFolderId] = useState(folderId);

  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const isRootFolder = currentFolderId === ROOT_FOLDER_ID;

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrentFolderId(folderId);
  }, [folderId]);

  const documentQueries = useDocumentQueries();
  // Hoist the optional access into a local: the React Compiler (oxc) strips the
  // `?.` when it lifts `item?.id` into a cache-dependency key, producing an
  // unguarded `item.id` that throws while `item` is undefined.
  const itemId = item?.id;
  const {
    data: documentList,
    isLoading: isLoadingDocumentList,
    error: documentListError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: currentFolderId,
    }),
    enabled: !isRootFolder && isOpened,
  });
  const {
    data: rootList,
    isLoading: isLoadingRootList,
    error: rootListError,
  } = useQuery({
    ...documentQueries.listRoots(),
    enabled: isRootFolder && isOpened,
  });
  const {
    data: fileDetails,
    isLoading: isLoadingFileDetails,
    error: fileDetailsError,
  } = useQuery({
    ...documentQueries.fileDetails({
      folderId,
      fileId: itemId || "-",
    }),
    enabled: item != null && item.type !== "folder" && isOpened,
  });
  const {
    data: folderDetails,
    isLoading: isLoadingFolderDetails,
    error: folderDetailsError,
  } = useQuery({
    ...documentQueries.folderDetails({
      folderId: itemId || "-",
    }),
    enabled: item != null && item.type === "folder" && isOpened,
  });
  const updateFolderMutation = documentMutations.useUpdateFolder(itemId || "-");
  const updateFileMutation = documentMutations.useUpdateFile(itemId || "-", currentFolderId);

  const handleSubmit = async () => {
    if (item == null) {
      return;
    }

    if (item.type === "folder") {
      if (!folderDetails) {
        return;
      }

      await updateFolderMutation.mutateAsync({
        nameTranslations: folderDetails.nameTranslations.map((translation) => ({
          ...translation,
          languageId: translation.languageId as LanguageIdDto,
        })),
        parentFolderId: currentFolderId,
      });
    } else {
      if (!fileDetails || !item.document) {
        return;
      }

      await updateFileMutation.mutateAsync({
        nameTranslations: fileDetails.nameTranslations.map((translation) => ({
          ...translation,
          languageId: translation.languageId as LanguageIdDto,
        })),
        parentFolderId: currentFolderId,
        uploadId: item.document.id,
      });
    }

    onOpenChange(false);
  };

  const error = rootListError || documentListError || fileDetailsError || folderDetailsError;

  useEffect(() => {
    if (item != null && error) {
      showFlashToast({
        message: t("component.documents.move-item-modal.notification.error"),
        variant: "alert",
      });

      onOpenChange(false);
    }
  }, [error, item, onOpenChange, showFlashToast, t]);

  const items =
    (isRootFolder ? rootList?.filter((f) => f.canAddItem) : documentList?.items.filter((x) => x.type === "folder")) ??
    [];

  const breadcrumbs = [
    {
      name: t("model.document.breadcrumbs.root"),
      id: ROOT_FOLDER_ID,
    },
    ...(documentList?.ancestors || []),
    ...(!isRootFolder && documentList
      ? [
          {
            name: documentList.name,
            id: currentFolderId,
          },
        ]
      : []),
  ]
    .filter(Boolean)
    .map(
      ({ name, id: currFolderId }) =>
        ({
          callback: () => setCurrentFolderId(currFolderId),
          name,
        }) satisfies Breadcrumb,
    );

  const isSubmitting = updateFileMutation.isPending || updateFolderMutation.isPending;
  const isLoading = isLoadingRootList || isLoadingDocumentList || isLoadingFolderDetails || isLoadingFileDetails;

  return (
    <Modal.Root
      isOpened={item != null}
      onOpenChange={onOpenChange}
      title={t("component.documents.move-item-modal.title")}
      size="sm"
      data-testid="documents-move-modal"
    >
      <Modal.Body>
        {item && (
          <Inline gap="sm" align="center">
            <DocumentsFileIcon type={item.type} />
            <span className="text-caption">{item.name}</span>
          </Inline>
        )}
        <Box padding={{ top: "sm", bottom: "md" }}>
          <Divider />
        </Box>
        <Breadcrumbs pages={breadcrumbs} />
        <RuntimeHeight minHeight={208}>
          {(heightAttributes) => (
            <section {...heightAttributes} className="my-space-md rounded-xs border border-border-secondary p-space-md">
              {isLoading && <FullSizeLoader />}
              {!isLoading && items.length === 0 && (
                <span className="text-caption text-text-secondary">
                  {t("component.documents.move-item-modal.list-empty")}
                </span>
              )}
              {!isLoading && items.length > 0 && (
                <Stack gap="sm" as="ul">
                  {items.map(({ id, name }) => (
                    <li key={id}>
                      <button
                        type="button"
                        className={buttonVariants({ variant: "ghostSecondary", size: "sm", fullWidth: true })}
                        onClick={() => setCurrentFolderId(id)}
                      >
                        <Inline gap="sm" align="center">
                          <DocumentsFileIcon type="folder" />
                          <span className="text-caption">{name}</span>
                        </Inline>
                      </button>
                    </li>
                  ))}
                </Stack>
              )}
            </section>
          )}
        </RuntimeHeight>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button
          data-testid="documents-move-modal-submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!isRootFolder && currentFolderId === folderId}
        >
          {t("component.documents.move-item-modal.action.move")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
